import type { GetOfferQuery } from 'src/common/api/apollo/additional';
import { parseHealthQuestionnaireAnswerValue } from './parseHealthQuestionnaireAnswerValue.util';
import type { AnyQuestion, GqlFollowUp, GqlQuestion, QuestionnaireAnswers, TableAnswer } from './healthQuestionnaire.types';

type OfferHealthQuestionnaire = NonNullable<
NonNullable<NonNullable<GetOfferQuery['getOffer']>['productTypeSpecific']>['healthQuestionnaire']
>;

type QuestionKind = 'yes_no' | 'checkbox' | 'checkbox_group' | 'text' | 'table';

function buildQuestionCodeTypeMap(questions: AnyQuestion[]): Map<string, QuestionKind> {
  const map = new Map<string, QuestionKind>();
  const walk = (qs: AnyQuestion[]) => {
    for (const q of qs) {
      const t = q.type?.toLowerCase() ?? '';
      if (t === 'yes_no_question' || t === 'yes_no' || t === 'boolean') {
        map.set(q.code, 'yes_no');
      } else if (t === 'checkbox' || t === 'checkboxes') {
        map.set(q.code, 'checkbox');
      } else if (t === 'checkbox_group' || t === 'multi_select') {
        map.set(q.code, 'checkbox_group');
      } else if (t === 'input_text' || t === 'input_number' || t === 'input_string') {
        map.set(q.code, 'text');
      } else if (t === 'table') {
        map.set(q.code, 'table');
      } else if (t === 'group_of_questions' || t === 'group' || t === 'section') {
        const children = ('questions' in q ? (q as GqlQuestion).questions : undefined) ?? [];
        walk(children as AnyQuestion[]);
      }
    }
  };
  walk(questions);
  return map;
}

/** API may save `followUp` only on a group; rows use child `questionCode` — map child → group's followUp.code. */
function collectQuestionCodeToFollowUpCode(questions: AnyQuestion[]): Record<string, string> {
  const map: Record<string, string> = {};
  const walk = (qs: AnyQuestion[]) => {
    for (const q of qs) {
      if ('followUp' in q && q.followUp?.code) {
        map[q.code] = q.followUp.code;
      }
      const children = ('questions' in q ? (q as GqlQuestion).questions : undefined) ?? [];
      const t = q.type?.toLowerCase() ?? '';
      if (children.length && t === 'group_of_questions' && 'followUp' in q && q.followUp?.code) {
        const g = q.followUp.code;
        for (const child of children) {
          const own = 'followUp' in child ? child.followUp?.code : undefined;
          if (!own) map[child.code] = g;
        }
      }
      if (children.length) walk(children as AnyQuestion[]);
    }
  };
  walk(questions);
  return map;
}

function collectAllFollowUps(questions: AnyQuestion[]): GqlFollowUp[] {
  const out: GqlFollowUp[] = [];
  const walk = (qs: AnyQuestion[]) => {
    for (const q of qs) {
      if ('followUp' in q && q.followUp) out.push(q.followUp);
      const children = ('questions' in q ? (q as GqlQuestion).questions : undefined) ?? [];
      if (children.length) walk(children as AnyQuestion[]);
    }
  };
  walk(questions);
  return out;
}

/** Last resort: match saved field codes to a follow-up definition. */
function followUpCodeFromFieldCodes(fieldCodes: string[], questions: AnyQuestion[]): string | undefined {
  if (fieldCodes.length === 0) return undefined;
  const unique = new Set(fieldCodes).size;
  const candidates = collectAllFollowUps(questions).filter((fu) => {
    const defs = new Set((fu.fields ?? []).map((f) => f.code));
    return fieldCodes.every((fc) => defs.has(fc));
  });
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0].code;
  const exact = candidates.find((fu) => (fu.fields ?? []).length === unique);
  if (exact) return exact.code;
  candidates.sort((a, b) => (a.fields?.length ?? 0) - (b.fields?.length ?? 0));
  return candidates[0].code;
}

/** When schema has no row for this code, infer from JSON/string shape. */
function inferKindFromParsed(parsed: unknown): QuestionKind {
  if (typeof parsed === 'boolean') return 'yes_no';
  if (Array.isArray(parsed)) return 'checkbox_group';
  if (parsed && typeof parsed === 'object') {
    const values = Object.values(parsed as Record<string, unknown>);
    if (values.length > 0 && values.every((v) => Array.isArray(v))) return 'table';
  }
  if (typeof parsed === 'string') {
    const s = parsed.trim().toLowerCase();
    if (s === 'true' || s === 'false' || s === 'yes' || s === 'no') return 'yes_no';
  }
  return 'text';
}

function toTableAnswer(parsed: unknown): TableAnswer {
  let source: unknown = parsed;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return {};
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
  const out: TableAnswer = {};
  for (const [rowCode, raw] of Object.entries(source as Record<string, unknown>)) {
    if (Array.isArray(raw)) {
      out[rowCode] = raw.map(String);
    }
  }
  return out;
}

function toYesNo(parsed: unknown): boolean {
  if (typeof parsed === 'boolean') return parsed;
  if (typeof parsed === 'string') {
    const s = parsed.trim().toLowerCase();
    if (s === 'true' || s === 'yes') return true;
    if (s === 'false' || s === 'no') return false;
  }
  if (parsed === 'true' || parsed === 'yes') return true;
  if (parsed === 'false' || parsed === 'no') return false;
  return Boolean(parsed);
}

function toCheckboxList(parsed: unknown): string[] {
  if (Array.isArray(parsed)) return parsed.map(String);
  if (typeof parsed === 'string') {
    try {
      const inner = JSON.parse(parsed);
      return Array.isArray(inner) ? inner.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function mergeFollowUpRow(
  fuCode: string,
  row: OfferHealthQuestionnaire['answers'][number],
  followUpFieldsOut: Record<string, Record<string, string>>,
): void {
  if (!row.followUpAnswers?.length) return;
  const merged = { ...(followUpFieldsOut[fuCode] ?? {}) };
  for (const fa of row.followUpAnswers) {
    const fv = parseHealthQuestionnaireAnswerValue(fa.value);
    merged[fa.fieldCode] = fv === null || fv === undefined ? '' : String(fv);
  }
  followUpFieldsOut[fuCode] = merged;
}

/**
 * Hydrates form state from saved offer answers.
 * - Prefer questionnaire schema (`type`) per `questionCode`.
 * - If code is missing from schema, infer from value (`"false"` → yes/no, not text).
 * - Map follow-ups: own question, group children, or field-code match.
 */
export function buildInitialQuestionnaireValuesFromOffer(
  questions: AnyQuestion[],
  healthQuestionnaire: OfferHealthQuestionnaire | null | undefined,
): Partial<QuestionnaireAnswers> {
  if (!healthQuestionnaire?.answers?.length) {
    return {};
  }

  const typeByCode = buildQuestionCodeTypeMap(questions);
  const codeToFollowUp = collectQuestionCodeToFollowUpCode(questions);

  const questionsOut: Record<string, boolean> = {};
  const checkboxesOut: Record<string, string[]> = {};
  const textInputsOut: Record<string, string> = {};
  const tablesOut: Record<string, TableAnswer> = {};
  const followUpFieldsOut: Record<string, Record<string, string>> = {};

  for (const row of healthQuestionnaire.answers) {
    const code = row.questionCode;
    const parsed = parseHealthQuestionnaireAnswerValue(row.value);
    const kind = typeByCode.get(code) ?? inferKindFromParsed(parsed);

    switch (kind) {
      case 'yes_no':
        questionsOut[code] = toYesNo(parsed);
        break;
      case 'checkbox':
      case 'checkbox_group':
        checkboxesOut[code] = toCheckboxList(parsed);
        break;
      case 'text':
        textInputsOut[code] = parsed === null || parsed === undefined ? '' : String(parsed);
        break;
      case 'table':
        tablesOut[code] = toTableAnswer(parsed);
        break;
      default:
        break;
    }

    let fuCode: string | undefined = codeToFollowUp[code];
    if (!fuCode && row.followUpAnswers?.length) {
      fuCode = followUpCodeFromFieldCodes(
        row.followUpAnswers.map((fa) => fa.fieldCode),
        questions,
      );
    }
    if (fuCode && row.followUpAnswers?.length) {
      mergeFollowUpRow(fuCode, row, followUpFieldsOut);
    }
  }

  const out: Partial<QuestionnaireAnswers> = {};
  if (Object.keys(questionsOut).length) out.questions = questionsOut;
  if (Object.keys(checkboxesOut).length) out.checkboxes = checkboxesOut;
  if (Object.keys(textInputsOut).length) out.textInputs = textInputsOut;
  if (Object.keys(tablesOut).length) out.tables = tablesOut;
  if (Object.keys(followUpFieldsOut).length) out.followUpFields = followUpFieldsOut;
  return out;
}
